import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import SearchBar from 'components/SearchBar/SearchBar';
import { Box } from '@mui/material';
import { Close } from '@mui/icons-material';
import styles from './SparaFilter.module.scss';

const FilterButton = ({
    label,
    icon: Icon,
    isActive,
    onClick,
    activeColor = '#2196f3',
    className = '',
}) => {
    const style = {
        '--active-color': activeColor,
        '--active-color-1a': `${activeColor}1a`,
        '--active-color-cc': `${activeColor}cc`,
    };

    return (
        <div
            className={`${styles.filterButton} ${className} ${
                isActive ? styles.filterButtonActive : ''
            }`}
            onClick={onClick}
            style={style}
        >
            {Icon && <Icon />}
            {label}
        </div>
    );
};

FilterButton.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    isActive: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    activeColor: PropTypes.string,
    className: PropTypes.string,
};

const FilterGroup = ({ children }) => {
    return <div className={styles.filterGroup}>{children}</div>;
};

FilterGroup.propTypes = {
    children: PropTypes.node.isRequired,
};

const SparaFilter = ({
    filters,
    onFilterClick,
    onClearAll,
    search,
    onSearchChange,
    t,
    showSearch = true,
    showSearchButton = false,
    filterGroups = [],
}) => {
    const [showAllFilters, setShowAllFilters] = useState(false);
    const hasActiveFilters = Object.values(filters).some((value) => value !== null);

    const handleClearAll = () => {
        onClearAll();
    };

    const renderFilterGroup = (group, index) => {
        if (!group || group.length === 0) return null;

        // Raða hópum þannig að valdir koma fyrst
        const sortedGroup = [...group].sort((a, b) => {
            const aFilterValue = filters[a.id];
            const bFilterValue = filters[b.id];
            const aIsActive = Array.isArray(aFilterValue)
                ? aFilterValue.includes(a.value)
                : aFilterValue === a.value;
            const bIsActive = Array.isArray(bFilterValue)
                ? bFilterValue.includes(b.value)
                : bFilterValue === b.value;
            if (aIsActive && !bIsActive) return -1;
            if (!aIsActive && bIsActive) return 1;
            return 0;
        });

        const visibleFilters = showAllFilters ? sortedGroup : sortedGroup.slice(0, 5);
        const hasMoreFilters = sortedGroup.length > 5;

        return (
            <React.Fragment key={index}>
                {index > 0 && <div className={styles.filterSeparator} />}
                <FilterGroup>
                    {visibleFilters.map((filter) => (
                        <FilterButton
                            key={filter.id}
                            label={t(filter.label)}
                            icon={filter.icon}
                            isActive={(() => {
                                const filterValue = filters[filter.id];
                                return Array.isArray(filterValue)
                                    ? filterValue.includes(filter.value)
                                    : filterValue === filter.value;
                            })()}
                            onClick={() => onFilterClick(filter.id, filter.value)}
                            activeColor={filter.color}
                        />
                    ))}
                    {hasMoreFilters && !showAllFilters && (
                        <FilterButton
                            label={t('showMore')}
                            onClick={() => setShowAllFilters(true)}
                            activeColor="#666"
                            className={styles.showMoreButton}
                        />
                    )}
                    {hasMoreFilters && showAllFilters && (
                        <FilterButton
                            label={t('showLess')}
                            onClick={() => setShowAllFilters(false)}
                            activeColor="#666"
                            className={styles.showMoreButton}
                        />
                    )}
                </FilterGroup>
            </React.Fragment>
        );
    };

    return (
        <div className={styles.filtersContainer}>
            {showSearch && (
                <Box sx={{ maxWidth: '300px' }}>
                    <SearchBar
                        value={search}
                        onChange={onSearchChange}
                        placeholder={t('search')}
                        showSearchButton={showSearchButton}
                    />
                </Box>
            )}
            {filterGroups
                .filter((group) => group && group.length > 0)
                .map((group, index) => renderFilterGroup(group, index))}
            {hasActiveFilters && (
                <div className={styles.clearAllButton} onClick={handleClearAll}>
                    <Close />
                </div>
            )}
        </div>
    );
};

SparaFilter.propTypes = {
    filters: PropTypes.object.isRequired,
    onFilterClick: PropTypes.func.isRequired,
    onClearAll: PropTypes.func.isRequired,
    search: PropTypes.string,
    onSearchChange: PropTypes.func,
    t: PropTypes.func.isRequired,
    showSearch: PropTypes.bool,
    filterGroups: PropTypes.arrayOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                label: PropTypes.string.isRequired,
                value: PropTypes.any.isRequired,
                icon: PropTypes.elementType,
                color: PropTypes.string,
            })
        )
    ).isRequired,
};

export default withTranslation('', { withRef: true })(SparaFilter);
